import { FC, ComponentProps, Ref, forwardRef, useMemo } from 'react';

import { getConflicts } from 'utils/booking';
import { isPastDate } from 'utils/date';
import Header from './header';
import Space from './space';
import SlotSelection from './slotSelection';
import LoadingOverlay from '../shared/loadingOverlay';

interface SpaceType {
  id: number;
  openingTimes?: { [key: string]: string };
}

interface Props {
  name: string;
  date: string;
  venue: any;
  spaces: SpaceType[];
  availability: any;
  disabled?: boolean;
  scrollRef: any;
  scrollState: any;
  times: string[];
  startTime?: string;
  endTime?: string;
  clinical?: boolean;
  slots?: ComponentProps<typeof SlotSelection>['slots'];
  bookingItems?: ComponentProps<typeof SlotSelection>['bookingItems'];
  onChangeSpace: (date: string, space: SpaceType) => void;
  onRemove: () => void;
  onRemoveSpace: (date: string, spaceId: number) => void;
  spaceDetailsComponent?: ComponentProps<typeof Space>['detailsComponent'];
  headerComponent?: typeof Header;
  readOnly?: boolean;
  loading?: boolean;
  touched?: object;
  setTouched?: (touched: object) => void;
  value?: object;
  setValue?: (value: object) => void;
}

const wrapSetter = (
  setter: (value: object) => void,
  value: object = {},
  key: string
) => (innerValue: any) => setter({ ...value, [key]: innerValue });

const Row: FC<Props> = (
  {
    name,
    date,
    times,
    venue,
    clinical,
    scrollRef,
    scrollState,
    startTime,
    endTime,
    slots = [],
    bookingItems = [],
    spaces,
    availability = {},
    disabled,
    onChangeSpace: changeSpace,
    onRemove,
    onRemoveSpace: removeSpace,
    spaceDetailsComponent,
    headerComponent: HeaderComponent = Header,
    loading = false,
    touched = {},
    setTouched = () => null,
    value = {},
    setValue = () => null,
  },
  forwardedRef: Ref
) => {
  const clashCount = useMemo(
    () =>
      spaces.map(({ id }) => getConflicts(availability[`s${id}`] || {})).flat()
        .length,
    [availability, spaces]
  );

  const bookingItemsBySpace = useMemo(
    () =>
      spaces
        .map(({ id }) => ({
          [`s${id}`]: bookingItems.filter(
            ({ space: { id: spaceId } = {} }) => spaceId === id
          ),
        }))
        .reduce((prev, curr) => ({ ...prev, ...curr }), {}),
    [spaces, bookingItems]
  );

  const renderField = (space, key) => {
    const slotSelectionProps = {
      name: `${name}.${key}`,
      date,
      clinical,
      venue,
      space,
      startTime,
      endTime,
      times,
      slots,
      bookingItems: bookingItemsBySpace[key],
      disabled,
      scrollRef,
      availability: availability[key],
      touched: touched[key],
      setTouched: wrapSetter(setTouched, touched, key),
      value: value[key],
      onChange: wrapSetter(setValue, value, key),
    };

    return loading ? null : <SlotSelection {...slotSelectionProps} />;
  };

  const isPastBooking = useMemo(() => isPastDate(date), [date]);

  const isBilled = space => {
    if (value[`s${space.id}`]) {
      const bookingItems = value[`s${space.id}`] || [];

      return bookingItems.some(bookingItem => !!bookingItem.statementState);
    }

    return false;
  };

  return (
    <div className="calendar__row" ref={forwardedRef}>
      <HeaderComponent date={date} clashCount={clashCount} onClick={onRemove} />

      {spaces.map(space => {
        const key = `s${space.id}`;

        return (
          <LoadingOverlay
            key={space.id}
            active={isPastBooking || isBilled(space)}
            spinner={<></>}
          >
            <Space
              space={space}
              times={times}
              scrollRef={scrollRef}
              scrollState={scrollState}
              clinical={clinical}
              setValue={wrapSetter(setValue, value, key)}
              setTouched={wrapSetter(setTouched, touched, key)}
              onChange={
                changeSpace ? () => changeSpace(date, space) : undefined
              }
              onRemove={
                removeSpace ? () => removeSpace(date, space.id) : undefined
              }
              detailsComponent={spaceDetailsComponent}
            >
              {renderField(space, key)}
            </Space>
          </LoadingOverlay>
        );
      })}
    </div>
  );
};

export default forwardRef(Row);
